import React, { useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { WholeState } from '../../store/common/type';
import { DDAgentListing, buildDDUrl } from '../../lib/ddListingTypes';
import {
  AgentListingsWrapper,
  AgentCarouselWrapper,
  AgentCarouselTrack,
  AgentCardLink,
  CardImageWrapper,
  CardImage,
  CardBody,
  CardTitle,
  CardSubtitle,
  CardPrice,
  CardRooms,
  CardRoomItem,
  CardBadge,
  ContactAgentButton,
  CarouselArrow,
  CardLastUpdated,
} from './UnitsFromAgent.style';
import {
  getSubtitle,
  getPropertyType,
  getBedrooms,
  getBathrooms,
  getCoverImage,
  getBedroomDisplay,
  getListingIdentifier,
} from './utility/ddListingsHelpers';
import { BedIcon } from './assets/BedIcon';
import { BathIcon } from './assets/BathIcon';
import { ExternalLinkIcon } from './assets/ExternalLinkIcon';
import { ChevronRightIcon } from './assets/ChevronRightIcon';
import { ChevronLeftIcon } from './assets/ChevronLeftIcon';
import {
  trackDDListingClick,
  trackDDListingCarouselArrow,
} from '../../common/analytics/analyticsTracker';
import { ADP } from '../../common/analytics/sourceTypes';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import dateTimeHelper from '../../common/helpers/ui/DateTimeHelper';
interface Props {
  listings: DDAgentListing[];
}

const UnitsFromAgent: React.FC<Props> = ({ listings }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const userInfo = useSelector((state: WholeState) =>
    state === undefined ? {} : state.common.user,
  );

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!trackRef.current) return;

    const track = trackRef.current;
    const firstCard = track.firstElementChild as HTMLElement | null;
    if (!firstCard) return;

    const trackGap = Number.parseFloat(
      getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0',
    );
    const scrollAmount = firstCard.offsetWidth + trackGap;

    track.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  if (!listings.length) return null;
  const showArrowsOnMobile = listings.length > 1;
  const showArrowsOnDesktop = listings.length > 3;
  const carouselListingIdentifier = getListingIdentifier(listings[0]);

  return (
    <AgentListingsWrapper>
      <AgentCarouselWrapper
        data-show-arrows-mobile={showArrowsOnMobile}
        data-show-arrows-desktop={showArrowsOnDesktop}
      >
        <CarouselArrow
          className="carousel-arrow prev"
          onClick={() => {
            scroll('left');
            trackDDListingCarouselArrow(
              userInfo,
              ADP.DD_LISTINGS,
              ANALYTICS_FEATURES.DD_LISTINGS_AGENT,
              carouselListingIdentifier,
              'prev',
            );
          }}
          aria-label="Previous"
        >
          <ChevronLeftIcon />
        </CarouselArrow>
        <AgentCarouselTrack ref={trackRef}>
          {listings.map((listing) => {
            const url = buildDDUrl(
              listing.urls?.listing?.desktopByLocales?.th ??
                listing.urls?.listing?.desktop,
            );
            const coverImage = getCoverImage(listing);
            const bedroomsCount = getBedrooms(listing);
            const bedrooms = getBedroomDisplay(bedroomsCount);
            const bathrooms = getBathrooms(listing);
            const subtitle = getSubtitle(listing);
            const propertyType = getPropertyType(listing);
            const title = listing.title?.en ?? listing.title?.th ?? '';
            const lastUpdated = listing.dates?.contentUpdated?.date ?? null;
            const listingIdentifier = getListingIdentifier(listing);

            return (
              <AgentCardLink
                key={listing.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackDDListingClick(
                    userInfo,
                    ADP.DD_LISTINGS,
                    ANALYTICS_FEATURES.DD_LISTINGS_AGENT,
                    listingIdentifier,
                    'view-listing',
                  )
                }
              >
                <CardImageWrapper>
                  {coverImage && (
                    <CardImage src={coverImage} alt={title} loading="lazy" />
                  )}
                </CardImageWrapper>

                <CardBody>
                  <CardTitle>{title}</CardTitle>
                  {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}

                  {listing.price?.formatted && (
                    <CardPrice>{listing.price.formatted}</CardPrice>
                  )}

                  <CardRooms>
                    {bedrooms !== null && (
                      <CardRoomItem>
                        <BedIcon />
                        {bedrooms}
                      </CardRoomItem>
                    )}
                    {bathrooms !== null && (
                      <CardRoomItem>
                        <BathIcon />
                        {bathrooms}
                      </CardRoomItem>
                    )}
                  </CardRooms>

                  {propertyType && <CardBadge>{propertyType}</CardBadge>}

                  <ContactAgentButton>
                    ติดต่อเอเจนต์ <ExternalLinkIcon height={12} width={12} />
                  </ContactAgentButton>
                  {lastUpdated && (
                    <CardLastUpdated>
                      อัปเดตล่าสุด:{' '}
                      {dateTimeHelper.getCustomDateTime(
                        lastUpdated,
                        'D MMMM YYYY',
                      )}
                    </CardLastUpdated>
                  )}
                </CardBody>
              </AgentCardLink>
            );
          })}
        </AgentCarouselTrack>
        <CarouselArrow
          className="carousel-arrow next"
          onClick={() => {
            scroll('right');
            trackDDListingCarouselArrow(
              userInfo,
              ADP.DD_LISTINGS,
              ANALYTICS_FEATURES.DD_LISTINGS_AGENT,
              carouselListingIdentifier,
              'next',
            );
          }}
          aria-label="Next"
        >
          <ChevronRightIcon />
        </CarouselArrow>
      </AgentCarouselWrapper>
    </AgentListingsWrapper>
  );
};

export default UnitsFromAgent;
